import { useCallback, useEffect, useState } from 'react';
import { Tag, Timeline, Typography } from 'antd';
import type { AuditLogEntry } from '@lens/shared';
import { getItemHistory } from './api.ts';

const { Text } = Typography;

interface AuditHistoryProps {
  spaceId: string;
  itemId: string;
}

const ACTION_LABELS: Record<string, string> = {
  created: 'Создание записи',
  field_change: 'Изменение поля',
  deleted: 'Удаление записи',
  status_change: 'Изменение статуса',
  bulk_update: 'Массовое обновление',
  link_created: 'Связь добавлена',
  link_deleted: 'Связь удалена',
  whiteboard_updated: 'Изменена доска',
  whiteboard_locked: 'Доска заблокирована',
  whiteboard_unlocked: 'Доска разблокирована',
  whiteboard_restored: 'Доска восстановлена из истории',
};

const ACTION_COLORS: Record<string, string> = {
  created: 'green',
  field_change: 'blue',
  deleted: 'red',
  status_change: 'orange',
  link_created: 'cyan',
  link_deleted: 'volcano',
  whiteboard_updated: 'purple',
  whiteboard_locked: 'purple',
  whiteboard_unlocked: 'purple',
  whiteboard_restored: 'purple',
};

const LINK_SOURCE_LABELS: Record<string, string> = {
  manual: 'вручную',
  rule: 'правилом',
  jira_pull: 'импортом из Jira',
};

function LinkChange({ details, resolvedRefs }: { details: Record<string, unknown>; resolvedRefs: Record<string, string> }): JSX.Element {
  const direction = typeof details.direction === 'string' ? details.direction : '';
  const otherItemId = typeof details.otherItemId === 'string' ? details.otherItemId : null;
  const otherItemType = typeof details.otherItemType === 'string' ? details.otherItemType : null;
  const linkType = typeof details.linkType === 'string' ? details.linkType : '—';
  const sourceKey = typeof details.source === 'string' ? details.source : 'manual';
  const ruleName = typeof details.ruleName === 'string' ? details.ruleName : null;
  const directionLabel = direction === 'incoming' ? 'из' : 'к';
  const otherItemLabel = otherItemId ? resolvedRefs[otherItemId] : null;
  return (
    <div style={{ fontSize: 12, marginTop: 2 }}>
      <Text type="secondary">{linkType}</Text>
      {' '}
      <Text type="secondary">{directionLabel}</Text>
      {' '}
      {otherItemId ? (
        <Text>
          {otherItemType ? `${otherItemType} · ` : ''}
          {otherItemLabel ?? <Text code style={{ fontSize: 11 }}>{otherItemId.slice(0, 8)}</Text>}
        </Text>
      ) : (
        <Text>—</Text>
      )}
      <Text type="secondary">
        {' · '}
        {LINK_SOURCE_LABELS[sourceKey] ?? sourceKey}
        {ruleName ? ` · «${ruleName}»` : ''}
      </Text>
    </div>
  );
}

type DiffPart = { text: string; type: 'equal' | 'removed' | 'added' };

/** Разбивает строку на токены-слова, сохраняя пробелы, чтобы можно было собрать обратно. */
function tokenize(str: string): string[] {
  return str.match(/\s+|\S+/g) ?? [];
}

/** Пословный diff через LCS: возвращает только реальные изменения, общий текст — как контекст. */
function diffWords(oldStr: string, newStr: string): DiffPart[] {
  const a = tokenize(oldStr);
  const b = tokenize(newStr);
  const n = a.length;
  const m = b.length;
  // LCS-таблица длин
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    const row = dp[i] ?? [];
    const rowNext = dp[i + 1] ?? [];
    for (let j = m - 1; j >= 0; j -= 1) {
      row[j] = a[i] === b[j] ? (rowNext[j + 1] ?? 0) + 1 : Math.max(rowNext[j] ?? 0, row[j + 1] ?? 0);
    }
  }
  const parts: DiffPart[] = [];
  const push = (text: string, type: DiffPart['type']): void => {
    const last = parts[parts.length - 1];
    if (last && last.type === type) last.text += text;
    else parts.push({ text, type });
  };
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push(a[i] ?? '', 'equal');
      i += 1;
      j += 1;
    } else if ((dp[i + 1]?.[j] ?? 0) >= (dp[i]?.[j + 1] ?? 0)) {
      push(a[i] ?? '', 'removed');
      i += 1;
    } else {
      push(b[j] ?? '', 'added');
      j += 1;
    }
  }
  while (i < n) {
    push(a[i] ?? '', 'removed');
    i += 1;
  }
  while (j < m) {
    push(b[j] ?? '', 'added');
    j += 1;
  }
  return parts;
}

const DIFF_ROW_BASE = {
  display: 'flex',
  gap: 6,
  padding: '1px 6px',
  borderRadius: 4,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
} as const;

const DIFF_MARK_BASE = {
  flex: '0 0 auto',
  fontFamily: 'monospace',
  userSelect: 'none',
  opacity: 0.7,
} as const;

function FieldDiff({ oldValue, newValue, resolvedRefs }: { oldValue: unknown; newValue: unknown; resolvedRefs: Record<string, string> }): JSX.Element {
  const oldStr = formatValue(oldValue, resolvedRefs);
  const newStr = formatValue(newValue, resolvedRefs);
  const parts = diffWords(oldStr, newStr);
  const changed = parts.some((p) => p.type !== 'equal');
  if (!changed) {
    return (
      <div style={{ fontSize: 12, marginTop: 2 }}>
        <Text>{newStr}</Text>
      </div>
    );
  }
  // Двухстрочный «было / стало»: красное и зелёное никогда не мешаются в одной строке.
  // Строка «было» = контекст + удалённое (зачёркнуто); строка «стало» = контекст + добавленное.
  const hasRemoved = parts.some((p) => p.type === 'removed');
  const hasAdded = parts.some((p) => p.type === 'added');
  return (
    <div style={{ fontSize: 12, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {hasRemoved && (
        <div style={{ ...DIFF_ROW_BASE, background: 'rgba(255, 77, 79, 0.08)' }}>
          <Text type="danger" style={DIFF_MARK_BASE}>−</Text>
          <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {parts
              .filter((p) => p.type !== 'added')
              .map((p, idx) =>
                p.type === 'removed' ? (
                  <Text key={idx} type="danger" delete>{p.text}</Text>
                ) : (
                  <Text key={idx} type="secondary">{p.text}</Text>
                ),
              )}
          </span>
        </div>
      )}
      {hasAdded && (
        <div style={{ ...DIFF_ROW_BASE, background: 'rgba(82, 196, 26, 0.10)' }}>
          <Text type="success" style={DIFF_MARK_BASE}>+</Text>
          <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {parts
              .filter((p) => p.type !== 'removed')
              .map((p, idx) =>
                p.type === 'added' ? (
                  <Text key={idx} type="success" strong>{p.text}</Text>
                ) : (
                  <Text key={idx} type="secondary">{p.text}</Text>
                ),
              )}
          </span>
        </div>
      )}
    </div>
  );
}

function formatValue(val: unknown, resolvedRefs: Record<string, string>): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'string') {
    if (val === '') return '—';
    return resolvedRefs[val] ?? val;
  }
  if (typeof val === 'number') return String(val);
  if (typeof val === 'boolean') return val ? 'Да' : 'Нет';
  if (Array.isArray(val)) {
    if (val.length === 0) return '—';
    return val
      .map((v) => (typeof v === 'string' ? (resolvedRefs[v] ?? v) : JSON.stringify(v)))
      .join(', ');
  }
  return JSON.stringify(val);
}

export function AuditHistory({ spaceId, itemId }: AuditHistoryProps): JSX.Element {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [resolvedRefs, setResolvedRefs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await getItemHistory(spaceId, itemId);
      setEntries(res.entries);
      setResolvedRefs(res.resolvedRefs ?? {});
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [spaceId, itemId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 24 }}>Загрузка...</div>;
  }

  if (entries.length === 0) {
    return <Text type="secondary">Нет записей в истории</Text>;
  }

  return (
    // Отступ сверху: первый узел таймлайна (кружок + бейдж) иначе прижимается
    // к верхнему краю скролл-зоны вкладок и подрезается overflow'ом.
    <div data-testid="audit-history" style={{ paddingTop: 6 }}>
      <Timeline
        items={entries.map((entry) => ({
          color: ACTION_COLORS[entry.action] ?? 'gray',
          children: (
            <div key={entry.id}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, minWidth: 0 }}>
                <Tag color={ACTION_COLORS[entry.action] ?? 'default'} style={{ marginInlineEnd: 0, flex: '0 0 auto' }}>
                  {ACTION_LABELS[entry.action] ?? entry.action}
                </Tag>
                {entry.fieldName && (
                  <Text
                    strong
                    style={{ fontSize: 13, flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={entry.fieldName}
                  >
                    {entry.fieldName}
                  </Text>
                )}
              </div>
              {entry.action === 'field_change' && (
                <FieldDiff oldValue={entry.oldValue} newValue={entry.newValue} resolvedRefs={resolvedRefs} />
              )}
              {(entry.action === 'link_created' || entry.action === 'link_deleted') && (
                <LinkChange details={entry.details} resolvedRefs={resolvedRefs} />
              )}
              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                {entry.author?.displayName ?? 'Система'} ·{' '}
                {new Date(entry.createdAt).toLocaleString('ru-RU')}
              </div>
            </div>
          ),
        }))}
      />
    </div>
  );
}
